import React, { useState, useEffect, useContext } from 'react';
import {
  createCategory,
  updateCategory,
  deleteCategory,
  getCategory,
} from '../../api/Api';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Loader from '../../Loader/Loader';
import { AuthContext } from '../../utils/AuthContext';

const Setting = () => {
  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const { selectedBranchId } = useContext(AuthContext);

  // FETCH CATEGORIES
  useEffect(() => {
    const fetchCategories = async () => {
      try {
       const response = await getCategory();

if (response?.data) {
  setCategories(response.data);
} else {
  console.error('Unexpected response structure:', response);
}
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error('Failed to fetch categories');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // OPEN ADD DIALOG
  const openDialog = () => {
    setCategoryName('');
    setIsEditing(false);
    setEditIndex(null);
    setIsDialogOpen(true);
  };

  // CLOSE DIALOG
  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  // SUBMIT CATEGORY
  const handleSubmit = async () => {
    if (!categoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    setSubmitting(true);

    try {
      // UPDATE CATEGORY
      if (isEditing) {
        const response = await updateCategory(
          categories[editIndex]._id,
          {
            name: categoryName,
            branch: selectedBranchId,
          }
        );

        const updatedCategories = [...categories];

        updatedCategories[editIndex] = response.data;

        setCategories(updatedCategories);

        toast.success('Category updated successfully!');
      }

      // CREATE CATEGORY
      else {
        const response = await createCategory({
          name: categoryName,
          branch: selectedBranchId,
        });

        setCategories([...categories, response.data]);

        toast.success('Category added successfully!');
      }

      closeDialog();

    } catch (error) {
      console.error('Error submitting category:', error);

      toast.error(
        error?.response?.data?.message ||
        'Failed to submit category'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // EDIT CATEGORY
  const handleEdit = (index) => {
    setCategoryName(categories[index].name);
    setEditIndex(index);
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  // OPEN DELETE DIALOG
  const openDeleteDialog = (index) => {
    setDeleteIndex(index);
    setIsDeleteDialogOpen(true);
  };

  // CLOSE DELETE DIALOG
  const closeDeleteDialog = () => {
    setDeleteIndex(null);
    setIsDeleteDialogOpen(false);
  };

  // DELETE CATEGORY
  const handleDelete = async () => {
    try {
      await deleteCategory(categories[deleteIndex]._id);

      const filteredCategories = categories.filter(
        (_, index) => index !== deleteIndex
      );

      setCategories(filteredCategories);

      toast.success('Category deleted successfully!');

    } catch (error) {
      console.error('Error deleting category:', error);

      toast.error(
        error?.response?.data?.message ||
        'Failed to delete category'
      );
    } finally {
      closeDeleteDialog();
    }
  };

  return (
    <>
      <div className="p-6 max-w-screen-xl mx-auto bg-white rounded-xl shadow-md min-h-screen">

        <ToastContainer position="top-right" autoClose={3000} />

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-[#16bab1]">
            Manage Categories
          </h1>

          <button
            onClick={openDialog}
            className="bg-[#16bab1] hover:bg-[#139a93] text-white px-4 py-2 rounded-lg shadow transition flex items-center gap-2"
          >
            <span className="text-lg font-bold">+</span>
            Add Category
          </button>
        </div>

        {/* TABLE */}
        {loading ? (
          <Loader />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">

            <table className="min-w-full bg-white">

              <thead className="bg-[#16bab1] text-white">
                <tr>
                  <th className="py-3 px-4 text-left text-sm">
                    S.No
                  </th>

                  <th className="py-3 px-4 text-left text-sm">
                    Category Name
                  </th>

                  <th className="py-3 px-4 text-center text-sm">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">

                {categories.length > 0 ? (
                  categories.map((category, index) => (
                    <tr
                      key={category._id}
                      className="hover:bg-gray-50"
                    >
                      <td className="py-3 px-4">
                        {index + 1}
                      </td>

                      <td className="py-3 px-4 font-medium">
                        {category.name}
                      </td>

                      <td className="py-3 px-4 flex justify-center gap-3">

                        <button
                          onClick={() => handleEdit(index)}
                          className="bg-blue-100 text-blue-600 px-3 py-1 rounded hover:bg-blue-200"
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => openDeleteDialog(index)}
                          className="bg-red-100 text-red-600 px-3 py-1 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>

                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="py-6 text-center text-gray-500"
                    >
                      No categories found
                    </td>
                  </tr>
                )}

              </tbody>

            </table>

          </div>
        )}

        {/* ADD / EDIT DIALOG */}
        {isDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">

            <div className="bg-white rounded-xl p-6 w-[90%] max-w-md shadow-2xl">

              <h2 className="text-xl font-bold mb-4">
                {isEditing
                  ? 'Edit Category'
                  : 'Add Category'}
              </h2>

              <input
                type="text"
                value={categoryName}
                onChange={(e) =>
                  setCategoryName(e.target.value)
                }
                placeholder="Enter category name"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#16bab1]"
              />

              <div className="flex justify-end gap-3 mt-6">

                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className={`px-4 py-2 rounded-lg text-white ${
                    submitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#16bab1] hover:bg-[#139a93]'
                  }`}
                >
                  {submitting
                    ? 'Saving...'
                    : isEditing
                    ? 'Update'
                    : 'Save'}
                </button>

              </div>

            </div>

          </div>
        )}

        {/* DELETE DIALOG */}
        {isDeleteDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">

            <div className="bg-white rounded-xl p-6 w-[90%] max-w-sm shadow-2xl">

              <h2 className="text-xl font-bold mb-3">
                Delete Category?
              </h2>

              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this category?
              </p>

              <div className="flex justify-end gap-3">

                <button
                  onClick={closeDeleteDialog}
                  className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
                >
                  Cancel
                </button>

                <button
                  onClick={handleDelete}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
                >
                  Delete
                </button>

              </div>

            </div>

          </div>
        )}

      </div>
    </>
  );
};

export default Setting;